import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TUTORIAL_STEP_TARGETS,
  TOTAL_TUTORIAL_STEPS,
  type TutorialStepTarget,
} from "../../config/eventTutorialDefaults";
import styles from "./EventTutorialGuide.module.css";

type EventTutorialGuideProps = {
  step: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
};

const getStepTarget = (step: number): TutorialStepTarget =>
  TUTORIAL_STEP_TARGETS[step] ?? "intro";

export const EventTutorialIntroBanner = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className={styles.introBanner} role="status">
      <span className={styles.introIcon} aria-hidden="true">
        🎯
      </span>
      <div>
        <p className={styles.introTitle}>{t("admin.eventTutorial.introTitle")}</p>
        <p className={styles.introText}>{t("admin.eventTutorial.introText")}</p>
      </div>
    </div>
  );
};

export const EventTutorialGuide = ({
  step,
  onNext,
  onBack,
  onSkip,
}: EventTutorialGuideProps): JSX.Element => {
  const { t } = useTranslation();
  const target = getStepTarget(step);
  const isFirst = step === 0;
  const isLast = step === TOTAL_TUTORIAL_STEPS - 1;

  useEffect(() => {
    if (target === "intro") return;
    const el = document.querySelector(`[data-tutorial="${target}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [step, target]);

  const stepKey = target === "intro" ? "intro" : target;

  return (
    <>
      <div className={styles.overlay} aria-hidden="true" />
      <div className={styles.panel} role="dialog" aria-labelledby="tutorial-title">
        <div className={styles.panelInner}>
          <div className={styles.header}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 mb-1">
                {t("admin.eventTutorial.badge", {
                  current: step + 1,
                  total: TOTAL_TUTORIAL_STEPS,
                })}
              </p>
              <h2 id="tutorial-title" className={styles.title}>
                {t(`admin.eventTutorial.steps.${stepKey}.title`)}
              </h2>
              <p className={styles.body}>
                {t(`admin.eventTutorial.steps.${stepKey}.body`)}
              </p>
              <div className={styles.progress} aria-hidden="true">
                {TUTORIAL_STEP_TARGETS.map((_, index) => (
                  <span
                    key={index}
                    className={`${styles.dot} ${index <= step ? styles.dotActive : ""}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <button type="button" onClick={onSkip} className={styles.linkBtn}>
              {t("admin.eventTutorial.skip")}
            </button>
            {!isFirst && (
              <button type="button" onClick={onBack} className={styles.secondaryBtn}>
                {t("admin.eventTutorial.back")}
              </button>
            )}
            <button type="button" onClick={onNext} className={styles.primaryBtn}>
              {isLast ? t("admin.eventTutorial.finish") : t("admin.eventTutorial.next")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export const getTutorialHighlightClass = (
  tutorialActive: boolean,
  step: number,
  target: TutorialStepTarget,
): string => {
  if (!tutorialActive) return "";
  const current = getStepTarget(step);
  return current === target ? styles.highlight : "";
};
